import React from 'react';
import {
  Calendar,
  ClipboardCheck,
  Award,
  FileText,
  Bell,
  Clock,
  TrendingUp,
  BookOpen,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const quickLinks = [
  { path: '/timetable', name: 'View Timetable', icon: Calendar, color: 'bg-blue-500' },
  { path: '/attendance', name: 'My Attendance', icon: ClipboardCheck, color: 'bg-green-500' },
  { path: '/results', name: 'View Results', icon: Award, color: 'bg-purple-500' },
  { path: '/assignments', name: 'Assignments', icon: FileText, color: 'bg-orange-500' },
];

const todaySchedule = [
  { time: '08:00 AM', subject: 'Mathematics', teacher: 'Mr. Johnson', room: 'Room 101' },
  { time: '09:00 AM', subject: 'Physics', teacher: 'Mrs. Smith', room: 'Lab 2' },
  { time: '10:00 AM', subject: 'English', teacher: 'Ms. Davis', room: 'Room 103' },
  { time: '11:30 AM', subject: 'Computer Science', teacher: 'Mr. Wilson', room: 'Lab 1' },
  { time: '01:00 PM', subject: 'History', teacher: 'Mr. Brown', room: 'Room 105' },
];

const recentNotifications = [
  { id: 1, title: 'Assignment Due Tomorrow', message: 'Math assignment submission deadline', time: '1 hour ago', type: 'warning' },
  { id: 2, title: 'Exam Schedule Released', message: 'Mid-term examination dates announced', time: '3 hours ago', type: 'info' },
  { id: 3, title: 'Fee Reminder', message: 'Quarterly fee payment due next week', time: '1 day ago', type: 'alert' },
];

const Dashboard = () => {
  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-6 sm:p-8 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">Welcome back, John!</h1>
            <p className="text-primary-100">Here's what's happening with your academics today.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center px-4 py-2 bg-white/10 rounded-xl backdrop-blur-sm">
              <p className="text-2xl font-bold">92%</p>
              <p className="text-xs text-primary-100">Attendance</p>
            </div>
            <div className="text-center px-4 py-2 bg-white/10 rounded-xl backdrop-blur-sm">
              <p className="text-2xl font-bold">A+</p>
              <p className="text-xs text-primary-100">Grade</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {quickLinks.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md hover:border-primary-200 transition-all group"
          >
            <div className={`${link.color} w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
              <link.icon size={24} className="text-white" />
            </div>
            <h3 className="font-semibold text-gray-900">{link.name}</h3>
          </Link>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Schedule */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock size={20} className="text-primary-600" />
                <h2 className="text-lg font-semibold text-gray-900">Today's Schedule</h2>
              </div>
              <span className="text-sm text-gray-500">Friday, Jan 17</span>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {todaySchedule.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-primary-50 transition-colors"
                >
                  <div className="text-center min-w-[80px]">
                    <p className="text-sm font-semibold text-primary-600">{item.time}</p>
                  </div>
                  <div className="w-1 h-12 bg-primary-200 rounded-full" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{item.subject}</h3>
                    <p className="text-sm text-gray-500">{item.teacher} • {item.room}</p>
                  </div>
                  <BookOpen size={18} className="text-gray-400" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell size={20} className="text-primary-600" />
                <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
              </div>
              <span className="px-2 py-1 bg-red-100 text-red-600 text-xs font-semibold rounded-full">3 New</span>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {recentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className="p-4 rounded-xl border border-gray-100 hover:border-primary-200 transition-colors cursor-pointer"
                >
                  <h3 className="font-medium text-gray-900 mb-1">{notification.title}</h3>
                  <p className="text-sm text-gray-500 mb-2">{notification.message}</p>
                  <p className="text-xs text-gray-400">{notification.time}</p>
                </div>
              ))}
            </div>
            <Link
              to="/notifications"
              className="block mt-4 text-center text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              View All Notifications
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-500">Pending Assignments</h3>
            <FileText size={20} className="text-orange-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">5</p>
          <p className="text-sm text-orange-500 mt-1">2 due this week</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-500">Average Score</h3>
            <TrendingUp size={20} className="text-green-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">87%</p>
          <p className="text-sm text-green-500 mt-1">+5% from last term</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-500">Classes Today</h3>
            <Calendar size={20} className="text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">5</p>
          <p className="text-sm text-blue-500 mt-1">Next: Physics at 9 AM</p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
